import type {Media} from "crqlar-react-common/dist/models/common/media.model";
import type {Survey} from "crqlar-react-common/dist/models/survey/survey.model";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {getScaledUrl, MediaSize} from "crqlar-react-common/dist/utils/media.util";

/**
 * Edge length of the logo overlaid on the code, as a fraction of the code's own edge length.
 * A centred square of this size hides 4% of the module area, well inside what level H recovers -
 * the cap is what keeps the code scannable, so both the on-screen overlay and the PNG export
 * derive their geometry from these ratios rather than hard-coded pixels.
 */
export const QR_LOGO_MAX_RATIO = 0.2

/** Breathing room between logo and the edge of its white pad, as a fraction of the logo box. */
export const QR_LOGO_PAD_RATIO = 0.08

/** Corner radius of the white pad, as a fraction of the logo box. */
export const QR_LOGO_RADIUS_RATIO = 0.12

/**
 * A length derived from the code's size, usable as a CSS value.
 * The fullscreen handover sizes its code with a CSS length ('min(70vw, 70vh)') so it tracks the
 * viewport, and nothing in JS knows the resulting pixels - calc() keeps the logo proportional
 * to whatever the browser resolves. Numeric sizes stay plain pixels.
 */
export const qrLogoLength = (size: number | string, ratio: number) =>
    typeof size === 'number' ? `${size * ratio}px` : `calc(${size} * ${ratio})`

const withUri = (...candidates: (Media | undefined)[]) => candidates.find(media => !!media?.mediaURI)

/**
 * The logo that brands a survey's QR handover, or undefined when the account has no imagery at all.
 *
 * The chain is continuity-first: the survey theme's own logo wins because that is the logo the
 * runner puts at the top of the questionnaire, so the poster the guest scans and the page it opens
 * carry the same mark. Failing that it falls back to the organization's imagery, in the order the
 * survey exports already use (`media`, the main landscape brand image, before the optional `logo`
 * slot) so a QR poster brands like the survey PDF and Word exports from the same account.
 *
 * Scaled through {@link getScaledUrl}: 300px is ample for a logo that renders at a fifth of the
 * code, and Cloudinary serves those derivatives with CORS headers, which is what lets the PNG
 * export draw the logo into a canvas without tainting it.
 */
export const useSurveyQrLogoUrl = (survey?: Survey): string | undefined => {
    const organization = useOrganization()

    const media = withUri(survey?.theme?.logoMedia, organization?.media, organization?.logo)

    return getScaledUrl(media, MediaSize.MEDIUM_300, MediaSize.MEDIUM_300) || undefined
}
